import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { getDefaultStore, useAtom } from "jotai";
import { useMemo, type JSX } from "react";

import { graphql } from "@repo/graphql-types/gql";

import {
  type RegulatoryStateOption,
  RegulatoryStateSelect,
} from "@/components/regulatory-documents/regulatory-state-select";
import { Button } from "@/components/ui/button/button";
import { ProgressRoundedLayout } from "@/components/ui/layout/progress-rounded-layout";
import { FullPageSpinner } from "@/components/ui/loading/full-page-spinner";
import {
  CardSelectDescription,
  CardSelectRoot,
  CardSelectTitle,
} from "@/components/ui/select-input/card-select";
import { useGraphQL } from "@/hooks/use-graphql";
import { regulatoryDocumentAtom } from "@/lib/atoms/regulatory-document-atom";
import {
  isReasonStepComplete,
  requiresLocationChoice,
} from "@/lib/form-schemas/regulatory-document-schema";
import {
  COD_TIPO_DECLARACAO_VINCULO,
  isBindingDeclaration,
  montaEstados,
} from "@/lib/helpers/regulatory-documents";
import { trackEvent } from "@/lib/tracking";

// biome-ignore lint/plugin/route-file-exports: predates the rule; move it out
export const RegulatoryDocumentStatesQuery = graphql(/* GraphQL */ `
  query RegulatoryDocumentStatesQuery($codUsuario: Int!, $codTipoDocumentoRegulatorio: Int!) {
    usuariosAgendas(where: { ativo: { _eq: true }, codUsuario: { _eq: $codUsuario } }) {
      tbUsuariosAgendasUnidades(where: { ativo: { _eq: true } }) {
        Unidade {
          codUnidade
          nomeLimpo
          siglaUF
          ativo
        }
      }
    }
    tbUFs {
      siglaUF
      nome
    }
    tiposDocumentosRegulatorios(
      where: { codTipoDocumentoRegulatorio: { eq: $codTipoDocumentoRegulatorio } }
    ) {
      unidades {
        codUnidade
        cadastroCnesCompleto
      }
    }
  }
`);

const RegulatoryDocumentStatePage = (): JSX.Element => {
  const navigate = useNavigate();
  const [draft, setDraft] = useAtom(regulatoryDocumentAtom);
  const { user } = Route.useRouteContext();

  const { data, isPending } = useGraphQL(RegulatoryDocumentStatesQuery, {
    codUsuario: user.codUsuario,
    codTipoDocumentoRegulatorio: COD_TIPO_DECLARACAO_VINCULO,
  });

  const states: RegulatoryStateOption[] = useMemo(() => montaEstados(data), [data]);

  const handleSelectionChange = (siglaUF: string): void => {
    const state = states.find((option) => option.siglaUF === siglaUF);

    setDraft((prev) => ({
      ...prev,
      siglaUF,
      stateLabel: state?.nome ?? "",
      coveredLocations: state?.unidades ?? [],
      selectedLocations: [],
    }));
  };

  const handleContinue = (): void => {
    if (!draft.siglaUF) return;

    trackEvent("Estado da Declaração Selecionado", {
      codTipoDocumentoRegulatorio: draft.codTipoDocumentoRegulatorio,
      siglaUF: draft.siglaUF,
      quantidadeUnidades: draft.coveredLocations.length,
    });

    void navigate({
      to: requiresLocationChoice({ ...draft, siglaUF: draft.siglaUF })
        ? "/settings/regulatory-documents/declaration-locations"
        : "/settings/regulatory-documents/review",
    });
  };

  return (
    <ProgressRoundedLayout progress={2 / 3} className="pb-0">
      <div className="flex h-full flex-col justify-between gap-4">
        <div className="flex flex-col gap-3">
          <CardSelectRoot
            name="regulatory-document-state"
            mode="single"
            defaultValue={draft.siglaUF || undefined}
            onChange={handleSelectionChange}
          >
            <CardSelectTitle>
              Para qual estado você precisa da declaração?
            </CardSelectTitle>
            <CardSelectDescription>
              Cada declaração contempla um estado e reúne as unidades da sua agenda nele.
            </CardSelectDescription>

            {isPending ? <FullPageSpinner /> : <RegulatoryStateSelect states={states} />}
          </CardSelectRoot>
        </div>

        <div className="sticky bottom-0 flex w-full flex-col bg-white pb-6 pt-4">
          <Button fullWidth onClick={handleContinue} disabled={!draft.siglaUF}>
            Continuar
          </Button>
        </div>
      </div>
    </ProgressRoundedLayout>
  );
};

export const Route = createFileRoute("/settings/regulatory-documents/state")({
  component: RegulatoryDocumentStatePage,
  beforeLoad: ({ context }) => {
    if (!context.flags["exibe-documentos-regulatorios-no-app"]) {
      redirect({ to: "/settings", throw: true });
    }

    const draft = getDefaultStore().get(regulatoryDocumentAtom);

    if (!isReasonStepComplete(draft)) {
      redirect({ to: "/settings/regulatory-documents", throw: true });
    }

    if (!isBindingDeclaration(draft.codTipoDocumentoRegulatorio)) {
      redirect({ to: "/settings/regulatory-documents/locations", throw: true });
    }
  },
});
